import { useState, useEffect } from "react";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useTheme } from "../context/ThemeContext"; // 🔥 Import useTheme
import API_BASE_URL from "../config";

const AddCustomerModal = ({ isOpen, onClose, onCustomerAdded }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [notes, setNotes] = useState("");
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [type, setType] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [agentError, setAgentError] = useState("");
  const { darkMode } = useTheme(); // 🔥 Access darkMode from useTheme

  useEffect(() => {
    if (isOpen) {
      setName("");
      setPhone("");
      setCity("");
      setCounty("");
      setBusinessType("");
      setNotes("");
      setAgentId("");
      setSuccessMessage("");
      setError(null);
      setNameError("");
      setPhoneError("");
      setAgentError("");
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Eroare la încărcarea utilizatorilor.");
        }

        const data = await response.json();
        setAgents(data);
      } catch (err) {
        console.error("Hiba az ügynökök és adminok lekérésekor:", err);
      }
    };

    if (isOpen) {
      fetchAgents();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    let valid = true;

    if (!name.trim()) {
      setNameError("Numele este obligatoriu!");
      valid = false;
    }

    if (!phone.match(/^\+?\d{7,15}$/)) {
      setPhoneError("Introduceți un număr de telefon valid!");
      valid = false;
    }

    if (!agentId) {
      setAgentError("Selectarea agentului este obligatorie!");
      valid = false;
    }

    if (!valid) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/admin-create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            phone,
            city,
            county,
            businessType,
            notes,
            agentId,
            type: parseInt(type, 10),
            status: 0,
            refusalReason: ""
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Eroare la adăugarea clientului.");
      }

      setSuccessMessage("✅ Client adăugat cu succes!");
      setTimeout(() => {
        setSuccessMessage("");
        onCustomerAdded();
        onClose();
      }, 500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center ${
        darkMode ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
      }`}
      onClick={onClose}
    >
      <div
        className={`p-6 rounded-3xl shadow-xl w-[600px] relative ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-black"
        }`} // 🔥 Conditional modal background and text color
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-red-500 hover:text-red-700 text-2xl"
        >
          ✖
        </button>

        <h2
          className={`text-2xl font-semibold mb-4 text-center ${
            darkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          Adăugare Client Nou
        </h2>

        {successMessage && (
          <p className="text-green-500 bg-green-900 p-2 rounded mb-3 text-center">
            {successMessage}
          </p>
        )}

        {error && (
          <p className="text-red-500 bg-red-900 p-2 rounded mb-3 text-center">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Nume<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Introduceți numele clientului"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              className={`w-full p-2 border border-gray-600 rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            />
            {nameError && (
              <p className="text-red-400 text-sm mt-1">{nameError}</p>
            )}
          </div>

          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Nr. Telefon<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Introduceți numărul de telefon"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError("");
              }}
              className={`w-full p-2 border border-gray-600 rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            />
            {phoneError && (
              <p className="text-red-400 text-sm mt-1">{phoneError}</p>
            )}
          </div>

          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Oraș
            </label>
            <input
              type="text"
              placeholder="Introduceți orașul"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={`w-full p-2 border border-gray-600 rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            />
          </div>

          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Județ
            </label>
            <input
              type="text"
              placeholder="Introduceți județul"
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              className={`w-full p-2 border border-gray-600 rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            />
          </div>

          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Tip Afacere
            </label>
            <input
              type="text"
              placeholder="Introduceți tipul de afacere"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              className={`w-full p-2 border border-gray-600 rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            />
          </div>

          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Tip Client<span className="text-red-500">*</span>
            </label>
            <select
              value={type}
              onChange={(e) => setType(parseInt(e.target.value, 10))}
              className={`w-full p-2 border rounded-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            >
              <option
                value="0"
                className={
                  darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                }
              >
                Client Final
              </option>
              <option
                value="1"
                className={
                  darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                }
              >
                Client Marmist
              </option>
            </select>
          </div>

          <div className="col-span-2">
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
            >
              Notițe
            </label>
            <textarea
              placeholder="Introduceți notițe"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full p-2 border border-gray-600 rounded-2xl resize-none h-24 ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
              }`}
            ></textarea>
          </div>

          <div className="col-span-2">
            <FormControl
              fullWidth
              sx={{
                bgcolor: darkMode ? "#1f2937" : "#fff",
                borderRadius: "6px",
                position: "relative",
              }}
            >
              <InputLabel
                sx={{
                  color: darkMode ? "white" : "black",
                  top: "-6px",
                  "&.MuiInputLabel-shrink": {
                    top: "-10px",
                  },
                }}
              >
                Responsabil Client<span className="text-red-500">*</span>
              </InputLabel>
              <Select
                value={agentId}
                onChange={(e) => {
                  const selected = agents.find(
                    (user) => user.id === parseInt(e.target.value, 10)
                  );
                  setAgentId(e.target.value);
                  setSelectedAgent(selected);
                  setAgentError("");

                  // 🔥 Csak akkor állítsa `Client Final`-ra, ha az `Agent` nem jogosult Marmurist-ra
                  if (
                    selected &&
                    selected.role === "Agent" &&
                    !selected.canAccessMarmurist
                  ) {
                    setType(0);
                  }
                }}
                MenuProps={{
                  disablePortal: true, // 💥 EZ A LÉNYEG
                }}
                sx={{
                  minHeight: "40px", // 📌 KISEBBÍTI A `SELECT` MAGASSÁGÁT
                  paddingTop: "5px", // 📌 KISEBBÍTI A FELSŐ TÁVOLSÁGOT
                  paddingBottom: "5px", // 📌 KISEBBÍTI AZ ALSÓ TÁVOLSÁGOT
                  display: "flex",
                  alignItems: "center",
                  color: darkMode ? "white" : "black",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
                }}
              >
                <MenuItem value="">Fără agent</MenuItem>
                {agents.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} ({user.email}) - {user.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {agentError && (
              <p className="text-red-400 text-sm mt-1">{agentError}</p>
            )}
          </div>

          <div className="col-span-2 flex justify-between gap-4 mt-4">
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: "#1976d2",
                borderRadius: "30px",
                color: "white",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { bgcolor: "#1565c0" },
              }}
              disabled={loading}
            >
              {loading ? "Se încarcă..." : "Adaugă Client"}
            </Button>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: darkMode ? "#fff" : "#000",
                borderColor: darkMode ? "#fff" : "#000",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { borderColor: darkMode ? "#ccc" : "#bbb" },
              }}
            >
              Închide
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
